import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IGetDishPortionInfo, TLanguageIDs, TPriceStrategy } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishPortionInfo } from 'backend/dishes';
import { IAppContext, AppContext } from 'appState/appContext';
import { editPortionInitialState, EditPortionContext, TTypeOfModalWindow } from './editPortionContext';
import { editPortionReducer } from './editPortionReducer';
import { EditPortionActionTypes } from './editPortionActions';
import { IViewDishContext, ViewDishContext } from '../../../../viewDishState/viewDishContext';
import { v4 as uuidv4 } from 'uuid';

export const EditPortionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editPortionReducer, editPortionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { dishFullInfo, restaurantIdForPortions, restaurantIdForRestaurantProps, selectedPortionId } =
		useContext<IViewDishContext>(ViewDishContext);
	const restaurantOfPortion = restaurantIdForPortions ?? restaurantIdForRestaurantProps;
	const { response, loading } = useFetchData<IGetDishPortionInfo, string>(
		selectedPortionId.length ? getDishPortionInfo : null,
		selectedPortionId,
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (dishFullInfo && response) {
			const defaultLanguage = dishFullInfo.translations.find((translation) => translation.isDefault);
			const defaultCustomerLanguage = defaultLanguage
				? {
						id: defaultLanguage.languageId,
						name: defaultLanguage.name || '',
				  }
				: null;

			const translations = response.data.translations.map((translation) => ({
				languageId: translation.languageId,
				name: translation.name,
			}));

			dispatch({
				type: EditPortionActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					portionId: selectedPortionId,
					nameOfPortion: response.data.name,
					price: response.data.price,
					priceStrategy: response.data.priceStrategy,
					externalId: response.data.externalId,
					isProtectedFromSync: response.data.isProtectedFromSync,
					isDisabled: response.data.isDisabled,
					posName: response.data.posName,
					articleNumber: response.data.articleNumber,
					fetchedCustomers: [dishFullInfo.customer],
					IDSelectedCustomer: dishFullInfo.customer.id,
					fetchedRestaurants: dishFullInfo.restaurants,
					IDSelectedRestaurant: restaurantOfPortion,
					nameOfDish: dishFullInfo.name,
					dishId: dishFullInfo.id,
					isCreatedOnPos: dishFullInfo.isCreatedOnPos,
					defaultCustomerLanguage,
					translations,
				},
			});
		}
	}, [dishFullInfo, response, restaurantOfPortion]);

	const changeNameOfPortion = useCallback((value: string) => {
		dispatch({
			type: EditPortionActionTypes.CHANGE_NAME_OF_PORTION,
			payload: {
				nameOfPortion: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditPortionActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const changePriceStrategy = useCallback((value: TPriceStrategy) => {
		dispatch({
			type: EditPortionActionTypes.CHANGE_PRICE_STRATEGY,
			payload: {
				priceStrategy: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: EditPortionActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: EditPortionActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const toggleIsProtectedFromSync = useCallback(() => {
		dispatch({
			type: EditPortionActionTypes.TOGGLE_IS_PROTECT_FROM_SYNC,
		});
	}, []);

	const toggleIsDisabled = useCallback(() => {
		dispatch({
			type: EditPortionActionTypes.TOGGLE_IS_DISABLED,
		});
	}, []);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: EditPortionActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditPortionActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setAdditionalRestaurants = (value: string[]) => {
		dispatch({
			type: EditPortionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditPortionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditPortionContext.Provider
			value={{
				changeNameOfPortion,
				changePrice,
				changePriceStrategy,
				changeExternalId,
				generateUniqueExternalId,
				toggleIsProtectedFromSync,
				toggleIsDisabled,
				setTranslationTab,
				changeNameTranslation,
				setAdditionalRestaurants,
				changeTypeOfModalWindow,
				portionId: state.portionId,
				nameOfPortion: state.nameOfPortion,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				nameOfDish: state.nameOfDish,
				dishId: state.dishId,
				posName: state.posName,
				articleNumber: state.articleNumber,
				isCreatedOnPos: state.isCreatedOnPos,
				price: state.price,
				priceStrategy: state.priceStrategy,
				externalId: state.externalId,
				isProtectedFromSync: state.isProtectedFromSync,
				isDisabled: state.isDisabled,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditPortionContext.Provider>
	);
};
